import { zodResolver } from '@hookform/resolvers/zod';
import { InfoCircle } from '@timelyapp/icons/solid-icons';
import {
  type ConnectionSettingsFormValues,
  ConnectionUpdateSchema,
  chooseSyncSetting,
  connectionSettingsFormSubmission,
  connectionSettingsFormValues,
  type SupportedSyncToTimelyEnums,
} from '@timelyapp/integration-cloud-api-spec';
import { Box, Checkbox, Form, Message, Text, Tooltip } from '@timelyapp/react';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import type { UseIntegrationModalManagerWithDetails } from '../../hooks/useIntegrationModalManager';
import { LEGACY_SYNC_SETTING_KEYS } from '../../utils/util.syncSettings';
import { buildFormattedSyncSettings } from '../../utils/util.syncSettingsDisplay';
import { IntegrationsSharedCustomFieldMappings } from './Integrations.Shared.CustomFieldMappings';
import { IntegrationsSharedFallbackClientDropdown } from './Integrations.Shared.FallbackClientDropdown';

export interface ConnectionSettingsFormProps {
  /** Config manager — WithDetails level works for both wizard and CM contexts */
  configManager: UseIntegrationModalManagerWithDetails;
  /** The connection ID to update. Callers provide this explicitly to avoid null assertions. */
  connectionId: string;
  /** Whether this is initial setup (affects defaultValues: operation defaults, profile key sync) */
  isInitialSetup: boolean;
  /** Whether user is a Timely employee (controls internal-only settings visibility) */
  isTimelyEmployee: boolean;
  /** Called after successful updateConnection mutation */
  onSuccess: () => void;
  /** Called when updateConnection mutation fails */
  onError: (error: { title: string; message: string }) => void;
  /** Called when form submission starts (before mutation) */
  onSubmitStart?: () => void;
  /** Called when form meta changes (used for Save/Next button UX) */
  onMetaChange?: (meta: { isDirty: boolean; isSubmitting: boolean }) => void;
  /** Called when form values change (used to surface live summary overrides) */
  onValuesChange?: (values: ConnectionSettingsFormValues) => void;
  /** Rendered at the bottom of the form body — used by wizard for "OK" button */
  children?: React.ReactNode;
}

export interface ConnectionSettingsFormHandle {
  /** Triggers form validation and submission */
  submit: () => void;
  /** Whether the form is currently submitting */
  isSubmitting: boolean;
  /** Whether any form values have been modified */
  isDirty: boolean;
  /** Returns current form values (snapshot, not reactive) */
  getValues: () => ConnectionSettingsFormValues;
}

export const ConnectionSettingsForm = React.forwardRef<
  ConnectionSettingsFormHandle,
  ConnectionSettingsFormProps
>((props, ref) => {
  const { configManager } = props;
  const { integrationDetails, active } = configManager;
  const { connection } = active;

  const providerTitle = configManager.providerTitle || integrationDetails.title || active.toolId;
  const activeProfile = active.activeProfile;

  const processedSettings = active.processedSyncSettings;
  const { allSettings, visibleSettings, settingsByKey } = processedSettings;

  const formattedSettings = useMemo(
    () =>
      buildFormattedSyncSettings({
        processedSettings,
        activeProfile,
        providerTitle,
        isTimelyEmployee: props.isTimelyEmployee,
      }),
    [processedSettings, activeProfile, providerTitle, props.isTimelyEmployee]
  );

  const isLegacySyncSetting = React.useCallback(
    (
      formatted: (typeof formattedSettings)[number]
    ): formatted is (typeof formattedSettings)[number] & {
      setting: { key: SupportedSyncToTimelyEnums };
    } => LEGACY_SYNC_SETTING_KEYS.includes(formatted.setting.key as SupportedSyncToTimelyEnums),
    []
  );

  const formattedLegacySyncSettings = useMemo(
    () => formattedSettings.filter(isLegacySyncSetting),
    [formattedSettings, isLegacySyncSetting]
  );

  const [updateConnection] = ticApi.useUpdateConnectionIdMutation();
  const [dependencyWarnings, setDependencyWarnings] = React.useState<string[]>([]);

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    setError,
    formState: { isSubmitting, isDirty, errors },
  } = useForm<ConnectionSettingsFormValues>({
    defaultValues: async () =>
      connectionSettingsFormValues(connection, {
        initialSetup: props.isInitialSetup,
        syncSettings: allSettings,
        importProfileKey:
          active.resolvedImportProfileKey ?? configManager.active.importProfileKey ?? null,
      }),
    resolver: zodResolver(ConnectionUpdateSchema.omit({ id: true })),
  });

  const onSubmit = async (formData: ConnectionSettingsFormValues) => {
    props.onSubmitStart?.();

    const submission = connectionSettingsFormSubmission(formData, props.isInitialSetup);

    try {
      await updateConnection({
        connectionId: props.connectionId,
        body: {
          id: props.connectionId,
          imported_data_access: submission.imported_data_access,
          settings: submission.settings,
        },
      }).unwrap();

      // Treat saved values as the new baseline (disables Save until further edits).
      reset(submission);

      props.onSuccess();
    } catch (error: any) {
      console.error('Server validation failed:', error);

      const issues: Array<{ path: string | string[]; message: string }> | undefined =
        error?.data?.issues;

      if (Array.isArray(issues)) {
        for (const issue of issues) {
          const path =
            typeof issue.path === 'string'
              ? issue.path
              : Array.isArray(issue.path)
                ? issue.path.join('.')
                : '';
          if (!path || typeof issue.message !== 'string') continue;
          setError(path as any, { type: 'server', message: issue.message } as any);
        }
      }

      const fieldErrors: Record<string, string> | undefined =
        error?.data?.fieldErrors || error?.data?.field_errors || error?.data?.errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        for (const [path, message] of Object.entries(fieldErrors)) {
          if (typeof message !== 'string') continue;
          setError(path as any, { type: 'server', message } as any);
        }
      }

      props.onError({
        title: 'Configuration Error',
        message:
          error?.data?.message || 'Failed to save configuration. Please check your settings.',
      });
    }
  };

  React.useEffect(() => {
    props.onMetaChange?.({ isDirty, isSubmitting });
  }, [props.onMetaChange, isDirty, isSubmitting]);

  React.useEffect(() => {
    if (!props.onValuesChange) return;
    const subscription = watch(values => {
      props.onValuesChange?.(values as ConnectionSettingsFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch, props.onValuesChange]);

  React.useImperativeHandle(
    ref,
    () => ({
      submit: () => handleSubmit(onSubmit)(),
      isSubmitting,
      isDirty,
      getValues: () => getValues(),
    }),
    [handleSubmit, onSubmit, isSubmitting, isDirty, getValues]
  );

  const showSyncOptions = visibleSettings.length > 0;
  const localSettings = watch('settings');
  const customFieldMappings = watch('settings.custom_field_mappings') ?? [];
  const supportsCustomSettingsFields = activeProfile?.supportsCustomSettingsFields ?? false;
  const clientMappingActive =
    supportsCustomSettingsFields &&
    customFieldMappings.some(mapping => mapping.target_resource_type === 'client');
  const clientSyncActive = watch('settings.sync_clients') === true || clientMappingActive;

  const handleSyncSettingChange = React.useCallback(
    (syncType: SupportedSyncToTimelyEnums, enabled: boolean) => {
      const choice = chooseSyncSetting(watch('settings') || {}, allSettings, syncType, enabled);

      if (choice.blockedBy.length > 0) {
        const conflictTitles = choice.blockedBy.map(setting => setting.label);
        setDependencyWarnings([
          `Cannot disable this setting - required by: ${conflictTitles.join(', ')}`,
        ]);
        setTimeout(() => {
          setDependencyWarnings([]);
        }, 5000);
        return;
      }

      for (const [key, value] of choice.changes) {
        setValue(`settings.${key}`, value, { shouldDirty: true });
      }

      // Show warning about auto-enabled dependencies
      if (choice.enabledDependencies.length > 0) {
        const depTitles = choice.enabledDependencies.map(dep => {
          const depSetting = settingsByKey.get(dep);
          return depSetting?.label || dep;
        });

        setDependencyWarnings(prev => [
          ...prev.filter(w => !w.startsWith('Auto-enabled')),
          `Auto-enabled dependencies: ${depTitles.join(', ')}`,
        ]);

        // Clear warning after 5 seconds
        setTimeout(() => {
          setDependencyWarnings(prev => prev.filter(w => !w.startsWith('Auto-enabled')));
        }, 5000);
      }
    },
    [setValue, watch, allSettings, settingsByKey]
  );

  return (
    <Form flexDirection="column" onSubmit={handleSubmit(onSubmit)}>
      <Box.Column gap="xlarge">
        {showSyncOptions && (
          <Box.Column padding="xlarge" gap="medium">
            <Box.Column gap="large">
              {formattedLegacySyncSettings.map(({ setting, displaySource }) => {
                // Only show dependencies that are not yet satisfied
                const unsatisfiedDeps = (setting.dependant_on ?? []).filter(
                  dep => localSettings?.[dep] !== true
                );
                const unsatisfiedDepTitles = unsatisfiedDeps.map(dep => {
                  const depSetting = settingsByKey.get(dep);
                  return depSetting?.label ?? dep;
                });
                const hasDependencies = unsatisfiedDepTitles.length > 0;

                const mappingOverride =
                  setting.key === 'sync_clients' && clientMappingActive
                    ? 'Required because custom field values are imported as clients'
                    : null;

                return (
                  <Box.Column key={setting.key} gap="xsmall">
                    <Checkbox
                      checked={
                        watch('settings')?.[setting.key] === true ||
                        (setting.key === 'sync_clients' && clientMappingActive)
                      }
                      onChangeChecked={checked => handleSyncSettingChange(setting.key, checked)}
                      disabled={!!mappingOverride}
                      id={`${setting.key}`}
                      data-testid={`setup-wizard-setting-${setting.key}`}
                    >
                      <Box.Row gap="small" alignItems="center">
                        <Text.Label as="span">{setting.label}</Text.Label>
                        {displaySource && (
                          <>
                            <Text color="subtle" size="small" style={{ marginLeft: 4 }}>
                              {displaySource}
                            </Text>
                            {setting.tooltip && (
                              <Tooltip tooltip={setting.tooltip}>
                                <InfoCircle
                                  width={14}
                                  height={14}
                                  color="var(--theme_color_content_subtle)"
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                        {!displaySource && setting.tooltip && (
                          <Box.Row gap="xsmall" alignItems="center">
                            <Tooltip tooltip={setting.tooltip}>
                              <InfoCircle
                                width={14}
                                height={14}
                                color="var(--theme_color_content_subtle)"
                              />
                            </Tooltip>
                          </Box.Row>
                        )}
                      </Box.Row>
                    </Checkbox>

                    {mappingOverride && (
                      <Box.Row gap="small" alignItems="center" style={{ marginLeft: 4 * 7 }}>
                        <Text color="subtle" size="xsmall">
                          ↳ {mappingOverride}
                        </Text>
                      </Box.Row>
                    )}

                    {hasDependencies && !mappingOverride && (
                      <Box.Row gap="small" alignItems="center" style={{ marginLeft: 4 * 7 }}>
                        <Text color="subtle" size="xsmall">
                          ↳ Depends on: {unsatisfiedDepTitles.join(', ')}
                        </Text>
                      </Box.Row>
                    )}
                  </Box.Column>
                );
              })}
            </Box.Column>
            {errors?.settings?.sync_projects && (
              <Form.Error>{errors.settings.sync_projects.message}</Form.Error>
            )}

            {/* Show dependency warnings */}
            {dependencyWarnings.map((warning, index) => (
              <Box.Row key={index} gap="small" alignItems="center">
                <InfoCircle width={16} height={16} color="var(--theme_color_content_warning)" />
                <Text color="subtle" size="small">
                  {warning}
                </Text>
              </Box.Row>
            ))}
          </Box.Column>
        )}

        {/* Advanced Section */}
        {(active.scopeData?.supports_webhooks || active.scope === 'account') && (
          <Box.Column>
            <Text.Headline size="xsmall">Advanced</Text.Headline>
            <Text color="subtle">Additional configuration options</Text>
            <Box.Column gap="large" padding="xlarge">
              {active.scope === 'account' && (
                <Box.Column gap="xsmall">
                  <Checkbox
                    checked={watch('settings.project_add_all_workspace_users') === true}
                    onChangeChecked={checked =>
                      setValue('settings.project_add_all_workspace_users', checked, {
                        shouldDirty: true,
                      })
                    }
                    disabled={watch('settings.sync_projects') !== true}
                    id="project_add_all_workspace_users"
                    data-testid="setup-wizard-setting-project_add_all_workspace_users"
                  >
                    <Text.Label as="span">Add all workspace users to projects</Text.Label>
                  </Checkbox>
                  <Box.Row gap="small" alignItems="center" style={{ marginLeft: 4 * 7 }}>
                    <Text color="subtle" size="xsmall">
                      ↳ Makes every user in your Timely workspace a member of each imported project
                    </Text>
                    <Tooltip tooltip="When importing projects, automatically add all users in your Timely workspace as members of each imported project.">
                      <InfoCircle
                        width={14}
                        height={14}
                        color="var(--theme_color_content_subtle)"
                      />
                    </Tooltip>
                  </Box.Row>
                </Box.Column>
              )}

              {active.scope === 'account' && (
                <Box.Column gap="xsmall">
                  <Checkbox
                    checked={watch('settings.project_auto_add_assignees_to_projects') === true}
                    onChangeChecked={checked =>
                      setValue('settings.project_auto_add_assignees_to_projects', checked, {
                        shouldDirty: true,
                      })
                    }
                    disabled={watch('settings.sync_tasks') !== true}
                    id="project_auto_add_assignees_to_projects"
                    data-testid="setup-wizard-setting-project_auto_add_assignees_to_projects"
                  >
                    <Text.Label as="span">Auto-add assignees to projects</Text.Label>
                  </Checkbox>
                  <Box.Row gap="small" alignItems="center" style={{ marginLeft: 4 * 7 }}>
                    <Text color="subtle" size="xsmall">
                      ↳ When importing tasks, auto-add missing task assignees to the project
                    </Text>
                    <Tooltip tooltip="When importing tasks, if a task has assignees that are not members of the project in Timely, automatically add them to the project before creating the task.">
                      <InfoCircle
                        width={14}
                        height={14}
                        color="var(--theme_color_content_subtle)"
                      />
                    </Tooltip>
                  </Box.Row>
                </Box.Column>
              )}

              {active.scope === 'account' && active.toolId === 'clickup' && (
                <Box.Column gap="xsmall">
                  <Checkbox
                    checked={watch('settings.restrict_tasks_to_assignees') === true}
                    onChangeChecked={checked =>
                      setValue('settings.restrict_tasks_to_assignees', checked, {
                        shouldDirty: true,
                      })
                    }
                    disabled={watch('settings.reference_tasks') !== true}
                    id="restrict_tasks_to_assignees"
                    data-testid="setup-wizard-setting-restrict_tasks_to_assignees"
                  >
                    <Text.Label as="span">Restrict task visibility to assignees</Text.Label>
                  </Checkbox>
                  <Box.Row gap="small" alignItems="center" style={{ marginLeft: 4 * 7 }}>
                    <Text color="subtle" size="xsmall">
                      ↳ Users only see the ClickUp tasks they are assigned to
                    </Text>
                    <Tooltip tooltip="When enabled, users see only the ClickUp tasks they are directly assigned to. Timely recognizes a user's ClickUp account when they connect ClickUp personally, or when their Timely user was created from that ClickUp account. Users without a recognized ClickUp account see no tasks.">
                      <InfoCircle
                        width={14}
                        height={14}
                        color="var(--theme_color_content_subtle)"
                      />
                    </Tooltip>
                  </Box.Row>
                </Box.Column>
              )}

              {/* Webhooks */}
              {active.scopeData?.supports_webhooks && (
                <Box.Column gap="xsmall">
                  <Checkbox
                    checked={watch('settings.sync_to_timely') === 'continuous'}
                    onChangeChecked={checked =>
                      setValue('settings.sync_to_timely', checked ? 'continuous' : 'on_demand', {
                        shouldDirty: true,
                      })
                    }
                    id="enable_realtime_updates"
                  >
                    <Text.Label as="span">Enable real-time updates</Text.Label>
                  </Checkbox>
                  <Box.Row gap="small" alignItems="center" style={{ marginLeft: 4 * 7 }}>
                    <Text color="subtle" size="xsmall">
                      ↳ Sync changes from {integrationDetails.title} automatically
                    </Text>
                  </Box.Row>
                  {errors?.settings?.sync_to_timely && (
                    <Form.Error>{errors.settings.sync_to_timely.message}</Form.Error>
                  )}
                </Box.Column>
              )}

              {/* Data Access Control */}
              {active.scope === 'account' && (
                <Box.Column gap="medium">
                  <Box.Column gap="xsmall">
                    <Checkbox
                      checked={watch('imported_data_access') === 'default'}
                      onChangeChecked={checked =>
                        setValue('imported_data_access', checked ? 'default' : 'everyone', {
                          shouldDirty: true,
                        })
                      }
                      id="restrict_data_access"
                    >
                      <Text.Label as="span">Restrict imported data</Text.Label>
                    </Checkbox>
                    <Box.Row gap="small" alignItems="center" style={{ marginLeft: 4 * 7 }}>
                      <Text color="subtle" size="xsmall">
                        ↳ Some functionality restrictions until individuals authenticate with
                        service.
                      </Text>
                    </Box.Row>
                  </Box.Column>
                  {watch('imported_data_access') === 'default' && (
                    <Message variant="warning" size="small" autoIcon>
                      <Box.Column gap="xsmall">
                        <Text size="small">Restricted access may impact functionality</Text>
                        <Text size="small" color="subtle">
                          Linked work references and imported data won't be available to team
                          members who haven't authenticated with {integrationDetails.title}.
                        </Text>
                      </Box.Column>
                    </Message>
                  )}
                </Box.Column>
              )}
            </Box.Column>
          </Box.Column>
        )}

        <Box.Column>
          <Text.Headline size="xsmall">
            {clientSyncActive ? 'Client settings' : 'Client fallback'}
          </Text.Headline>
          <Text color="subtle">
            If client relationship is unclear, projects may get assigned to...
          </Text>
          <Box.Column gap="medium" padding="xlarge">
            <Box.Row gap="large" alignItems="center">
              <Text.Label as="span" style={{ width: 180 }}>
                Fallback client
              </Text.Label>
              <Box.Column flexGrow={1}>
                <IntegrationsSharedFallbackClientDropdown
                  value={watch('settings.fallback_timely_client_id')}
                  onChange={clientId =>
                    setValue('settings.fallback_timely_client_id', clientId, { shouldDirty: true })
                  }
                />
              </Box.Column>
            </Box.Row>
            {supportsCustomSettingsFields && (
              <Box.Row gap="large" alignItems="flex-start">
                <Box.Column gap="xsmall" flex="0 0 180px">
                  <Text.Label as="span" style={{ width: 180, paddingTop: 4 }}>
                    Custom fields as clients&nbsp;
                  </Text.Label>
                  <Text size="small" color="subtle">
                    Create clients in Timely based on field values
                    <Tooltip
                      style={{ display: 'inline-block', marginLeft: 4 }}
                      tooltip={
                        <div style={{ maxWidth: 200 }}>
                          Client hierarchy of imported data is unchanged; this setting only creates
                          client entries in Timely based on custom field values.
                        </div>
                      }
                    >
                      <InfoCircle
                        width={14}
                        height={14}
                        color="var(--theme_color_content_subtle)"
                      />
                    </Tooltip>
                  </Text>
                </Box.Column>
                <Box.Column flexGrow={1}>
                  <IntegrationsSharedCustomFieldMappings
                    connectionId={props.connectionId}
                    targetResourceType="client"
                    value={customFieldMappings}
                    onChange={mappings => {
                      setValue('settings.custom_field_mappings', mappings, {
                        shouldDirty: true,
                      });
                      if (mappings.some(mapping => mapping.target_resource_type === 'client')) {
                        setValue('settings.sync_clients', true, { shouldDirty: true });
                      }
                    }}
                    disabled={isSubmitting}
                  />
                </Box.Column>
              </Box.Row>
            )}
          </Box.Column>
        </Box.Column>

        {props.children}
      </Box.Column>
    </Form>
  );
});

ConnectionSettingsForm.displayName = 'ConnectionSettingsForm';
